import { useParams, Link, Outlet } from 'react-router-dom';
import { User } from 'lucide-react';

import { DOCUMENT_TYPE } from '@/app/document/enums';
import { useDocuments } from '@/data/document';

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator
} from '@/components/ui/breadcrumb';
import { Card, CardContent } from '@/components/ui/card';
import LoadingPage from '@/components/common/LoadingPage';
import { Button } from '@/components/ui/button';

import ImageCarousel from './components/view/ImageCarousel';
import Navigation from './components/view/Navigation';

import { useCoach } from '@/data/coach';

const ViewPage = () => {
  const params = useParams();
  const coach = useCoach(params.id);

  return (
    <>
      <Breadcrumb className="mb-4">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/coach">Coaches</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>
              {coach.data.user.firstname} {coach.data.user.lastname}
            </BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
      <Card>
        <CardContent className="p-0">
          <div className="flex justify-start pt-8 pb-6 px-6">
            <div className="w-1/2">
              <div className="mb-10">
                <div className="flex items-center gap-4">
                  <h3 className="text-2xl font-bold">
                    {coach.data.user.firstname} {coach.data.user.lastname}{' '}
                    {coach.data.preferredName
                      ? `(${coach.data.preferredName})`
                      : ''}
                  </h3>
                  <Link to={`/user/${coach.data.user.id}`}>
                    <Button variant="outline">
                      <User className="w-4 h-4 mr-2" />
                      View User
                    </Button>
                  </Link>
                </div>

                <p className="text-muted-foreground">{coach.data.user.email}</p>
                <p className="text-muted-foreground">{coach.data.phone}</p>
              </div>
              <div className="max-w-[460px]">
                <div className="mb-6">
                  <h4 className="text-md font-bold">Short Bio</h4>
                  <p className="text-muted-foreground">{coach.data.bioShort}</p>
                </div>
              </div>
            </div>
            <div className="w-1/2 px-4">
              <h3 className="text-lg font-bold mb-4">Profile Images</h3>
              <div className="flex justify-center">
                <div className="w-fit">
                  <ImageCarousel profileImages={coach.data.profileImages} />
                </div>
              </div>
            </div>
          </div>
          <hr className="mt-2 mb-4" />
          <div className="pb-12 px-6">
            <h4 className="text-md font-bold">Full Bio</h4>
            <p className="text-muted-foreground">{coach.data.bioFull}</p>
          </div>
          <Navigation />
          <div className="px-6 py-8">
            <Outlet />
          </div>
        </CardContent>
      </Card>
    </>
  );
};

const ViewPageLoader = () => {
  const params = useParams();
  const coach = useCoach(params.id);
  const identityDocuments = useDocuments({
    coachId: params.id,
    type: DOCUMENT_TYPE.IDENTIFICATION
  });
  const firstAidDocuments = useDocuments({
    coachId: params.id,
    type: DOCUMENT_TYPE.FIRST_AID
  });

  if (coach.isLoading && !coach.data) {
    return <LoadingPage />;
  }
  if (identityDocuments.isLoading && !identityDocuments.data) {
    return <LoadingPage />;
  }
  if (firstAidDocuments.isLoading && !firstAidDocuments.data) {
    return <LoadingPage />;
  }

  return <ViewPage />;
};

export default ViewPageLoader;
